import { useEffect, useRef } from "react";
import { apiUrl } from "../constants/Constants";

// Convert the REST base URL to a WebSocket URL
// https://drdev.utilstop.com/ → wss://drdev.utilstop.com/ws
const WS_BASE = apiUrl
  .replace(/^https:\/\//, "wss://")
  .replace(/^http:\/\//, "ws://")
  .replace(/\/$/, "");

/**
 * useWebSocket — connects to the backend WebSocket, calls onEvent for every
 * server message, and auto-reconnects with exponential backoff on disconnect.
 *
 * @param {object}   opts
 * @param {function} opts.onEvent  - called with the parsed JSON payload on every server message
 * @param {function} [opts.onOpen] - called every time the socket (re)connects, including
 *                                   reconnects after a drop. Any event pushed while we were
 *                                   disconnected has no replay mechanism on the server, so the
 *                                   caller should treat this as a cue to resync state from the
 *                                   REST API rather than relying solely on onEvent.
 * @param {boolean}  [opts.enabled=true] - set false to skip connecting (e.g. user not logged in)
 */
export function useWebSocket({ onEvent, onOpen, enabled = true }) {
  // Always keep refs to the latest callbacks so reconnects don't need to
  // capture stale closures.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!enabled) return;

    let ws = null;
    let alive = true;
    let delay = 1000; // reconnect delay starts at 1 s
    let reconnectTimer = null;
    let pingTimer = null;
    let watchdogTimer = null;
    let lastActivity = Date.now();

    const PING_INTERVAL_MS = 25000;
    // Longer than two ping cycles so a server that never echoes pong isn't
    // mistaken for dead, but short enough to recover quickly from a real drop.
    const STALE_AFTER_MS = PING_INTERVAL_MS * 2 + 10000;

    function connect() {
      if (!alive) return;

      const token = localStorage.getItem("token");
      if (!token) {
        // No token yet — e.g. this component mounted before login finished,
        // or the token was cleared and will be restored shortly. Keep
        // retrying with the same backoff instead of giving up forever.
        schedule();
        return;
      }

      try {
        // Pass auth token as a query param — WebSocket handshake can't carry
        // custom headers, so this is the standard pattern.
        ws = new WebSocket(`${WS_BASE}/ws?token=${encodeURIComponent(token)}`);
      } catch {
        schedule();
        return;
      }

      ws.onopen = () => {
        if (!alive) {
          ws.close();
          return;
        }
        delay = 1000; // reset backoff on successful connection
        lastActivity = Date.now();

        // The server has no replay/backlog for events pushed while we were
        // disconnected, so every (re)connect is a cue to resync from REST —
        // otherwise a message that arrives during a drop (sleep, wifi
        // switch, the server's own 30s heartbeat evicting a stale socket)
        // is silently missed until the user manually reloads the page.
        onOpenRef.current?.();

        // Send a lightweight ping every 25 s to keep NAT/load-balancer alive.
        // Most servers simply ignore or echo it; some reply with {type:"pong"}.
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);

        // Watchdog for "zombie" connections: some proxies/load balancers or
        // NATs silently drop an idle socket without ever sending a close
        // frame, so readyState keeps reporting OPEN and onclose/onerror
        // never fire on their own — the client just sits there receiving
        // nothing forever, which looks exactly like "messages only show up
        // after I refresh the page." If we haven't heard anything at all
        // from the server (a real event or a pong) within STALE_AFTER_MS,
        // force-close the socket so the normal reconnect path takes over.
        watchdogTimer = setInterval(() => {
          if (Date.now() - lastActivity > STALE_AFTER_MS) {
            ws.close();
          }
        }, 10000);
      };

      ws.onmessage = (e) => {
        if (!alive) return;
        lastActivity = Date.now();
        try {
          const data = JSON.parse(e.data);
          console.log("------data---------", data);
          // Filter out server-side pong replies before forwarding to caller
          if (data.type !== "pong") onEventRef.current?.(data);
        } catch {
          // Ignore unparseable frames (e.g. plain-text "pong") — still
          // counts as activity above, since it proves the link is alive.
        }
      };

      // onerror always fires before onclose, so we only need to reconnect in onclose
      ws.onerror = () => {};

      ws.onclose = () => {
        clearInterval(pingTimer);
        clearInterval(watchdogTimer);
        pingTimer = null;
        watchdogTimer = null;
        if (alive) schedule();
      };
    }

    function schedule() {
      reconnectTimer = setTimeout(() => {
        delay = Math.min(delay * 2, 30000); // cap at 30 s
        connect();
      }, delay);
    }

    connect();

    return () => {
      alive = false;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      clearInterval(watchdogTimer);
      ws?.close();
    };
  }, [enabled]); // re-run only if enabled changes
}
